import type { CaptainSave, WindState, VoyageEvent, Sighting, DeployedNet, SailLevel, CrewMember, WreckMemorial, HurricaneSnapshot, FogBankSnapshot } from '@realseas/shared';
import { getOrigin, getPort, BRIG_CAPACITY, HURRICANE_BELT, HURRICANE_SEASON_MONTHS, SYPHILIS_METER_MAX } from '@realseas/shared';
import type { Connection } from '../Connection.js';
import { setHTML, esc } from '../dom.js';
import { openMapModal, closeMapModal, refreshMapIfOpen } from './MapView.js';
import { openHowToPlay } from './HowToPlayModal.js';

function ownedOf(captain: CaptainSave, itemId: string): number {
  return captain.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function compassName(rad: number): string {
  const deg = ((rad * 180 / Math.PI) % 360 + 360) % 360;
  const dirs = ['E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW', 'N', 'NNE', 'NE', 'ENE'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function formatEventTime(eventMs: number, embarkedMs: number | null): string {
  if (embarkedMs === null) return '';
  const elapsed = Math.max(0, eventMs - embarkedMs);
  const day = Math.floor(elapsed / 86400000);
  const d = new Date(eventMs);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `Day ${day}, ${hh}:${mm}`;
}

function netAge(deployedAt: number, serverTime: number): string {
  const ms = Math.max(0, serverTime - deployedAt);
  return formatElapsed(ms);
}

// ── Sea canvas animation
// Throttled to ~30fps and paused when scrolled off-screen, to keep scroll smooth.
// The renderer reacts to live weather state (wind speed, fog, hurricane).
let seaAnimHandle: number | null = null;
let seaObserver: IntersectionObserver | null = null;
let seaVisible: boolean = true;
const FRAME_INTERVAL_MS = 33;

interface SeaWeather {
  windSpeedKnots: number;       // 0 doldrums, 8 nominal, 22 gale, 32+ storm
  windDirRad: number;           // game convention (0=E, π/2=S, π=W, 3π/2=N)
  fogged: boolean;              // fog bank rolling over the ship
  hurricane: boolean;           // currently in hurricane belt + season
  sailLevel: 'full' | 'half' | 'reefed' | 'anchor' | 'row';
  hullFrac: number;             // 0–1, drives a slight list-to-port at low hull
  /** Local hour at the ship's longitude (0–24). Drives sun/moon, sky tone. */
  localHour: number;
  /** True when player has a whale on the line — show towing animation. */
  whaling: boolean;
  /** Wall-clock ms until the rod animation should remain visible (cast then idle). */
  rodActiveUntil: number;
  /** Net buoys to draw behind the ship. */
  netsDeployed: number;
  /** Wall-clock ms while the crew is in the water after `send_swim`. */
  swimUntil: number;
}
const seaWeather: SeaWeather = {
  windSpeedKnots: 8, windDirRad: Math.PI, fogged: false,
  hurricane: false, sailLevel: 'full', hullFrac: 1,
  localHour: 12, whaling: false, rodActiveUntil: 0, netsDeployed: 0, swimUntil: 0,
};

export function setSeaWeather(w: Partial<SeaWeather>): void {
  Object.assign(seaWeather, w);
}

// Dev hatch — expose the sea-state mutator on window so you can drive demos
// from the browser console. Harmless in prod; no calls are made from game code.
if (typeof window !== 'undefined') {
  (window as unknown as { setSeaWeather?: typeof setSeaWeather }).setSeaWeather = setSeaWeather;
}

function stopSeaAnimation(): void {
  if (seaAnimHandle !== null) {
    cancelAnimationFrame(seaAnimHandle);
    seaAnimHandle = null;
  }
  if (seaObserver) {
    seaObserver.disconnect();
    seaObserver = null;
  }
}

// One reusable rain-drop pool to keep allocation cheap.
const rainDrops: { x: number; y: number; vx: number; vy: number }[] = [];

function startSeaAnimation(canvas: HTMLCanvasElement): void {
  stopSeaAnimation();
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const start = performance.now();

  // Pause animation when canvas scrolls out of view
  seaVisible = true;
  if (typeof IntersectionObserver !== 'undefined') {
    seaObserver = new IntersectionObserver((entries) => {
      seaVisible = entries[0]?.isIntersecting ?? true;
    }, { threshold: 0 });
    seaObserver.observe(canvas);
  }

  let lastFrame = 0;
  let nextLightningAt = 0;
  let lightningFlash = 0;

  const draw = (now: number): void => {
    seaAnimHandle = requestAnimationFrame(draw);
    if (!seaVisible) return;
    if (now - lastFrame < FRAME_INTERVAL_MS) return;
    const dt = lastFrame === 0 ? 0.033 : (now - lastFrame) / 1000;
    lastFrame = now;
    const t = (now - start) / 1000;

    const wind = seaWeather.windSpeedKnots;
    const isDoldrums = wind < 1.5;
    const isGale = wind >= 22;
    const isStorm = wind >= 32 || seaWeather.hurricane;

    // Sky color shifts with wind AND time of day. Daylight runs 0 (midnight) → 1 (noon).
    const localHour = seaWeather.localHour;
    const daylight = (Math.cos((localHour - 12) / 12 * Math.PI) + 1) / 2;
    // Dawn/dusk are within a couple hours of sun rise/set — push warm horizon glow.
    const hoursToDawn = Math.min(Math.abs(localHour - 6), Math.abs(localHour - 6 + 24), Math.abs(localHour - 6 - 24));
    const hoursToDusk = Math.min(Math.abs(localHour - 18), Math.abs(localHour - 18 + 24), Math.abs(localHour - 18 - 24));
    const goldenHour = Math.max(0, 1 - Math.min(hoursToDawn, hoursToDusk) / 1.5);
    // Base sky tones: noon → midnight, blended.
    const dayTop = '#7298b6', dayMid = '#3a5a72', daySeaDeep = '#1f3a4f';
    const nightTop = '#0d1a2e', nightMid = '#172233', nightSeaDeep = '#0a1422';
    let baseTop = mixColor(nightTop, dayTop, daylight);
    let baseMid = mixColor(nightMid, dayMid, daylight);
    let baseSeaDeep = mixColor(nightSeaDeep, daySeaDeep, daylight);
    // Warm golden-hour push on the sky near horizon
    if (goldenHour > 0.05) {
      baseMid = mixColor(baseMid, '#d97a3a', goldenHour * 0.45);
    }
    // Storm darkens everything on top.
    const stormness = Math.max(0, Math.min(1, (wind - 12) / 20)) +
      (seaWeather.hurricane ? 0.5 : 0);
    const skyTop = mixColor(baseTop, '#1a1825', Math.min(1, stormness));
    const skyMid = mixColor(baseMid, '#241827', Math.min(1, stormness));
    const seaDeep = mixColor(baseSeaDeep, '#121426', Math.min(1, stormness));
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
    skyGrad.addColorStop(0,    skyTop);
    skyGrad.addColorStop(0.4,  skyMid);
    skyGrad.addColorStop(1,    seaDeep);
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, h);

    // ── Sun / moon — only visible when storm hasn't blotted everything out
    const celestialVis = Math.max(0, 1 - stormness * 1.4) * (seaWeather.fogged ? 0.2 : 1);
    if (celestialVis > 0.05) {
      const horizonY = h * 0.4;
      const arcTop = h * 0.08;
      if (localHour >= 6 && localHour < 18) {
        // Sun arcs east → west
        const sunFrac = (localHour - 6) / 12;
        const sx = sunFrac * w;
        const sy = horizonY - Math.sin(sunFrac * Math.PI) * (horizonY - arcTop);
        const sunR = 16 + (1 - daylight) * 8; // larger / hazier near horizon
        const sunCore = goldenHour > 0.3 ? '#ffd87a' : '#ffe9b0';
        const sunGlow = ctx.createRadialGradient(sx, sy, 0, sx, sy, sunR * 2.4);
        sunGlow.addColorStop(0,    `rgba(255, 224, 160, ${0.85 * celestialVis})`);
        sunGlow.addColorStop(0.35, `rgba(255, 180, 100, ${0.35 * celestialVis})`);
        sunGlow.addColorStop(1,    `rgba(255, 180, 100, 0)`);
        ctx.fillStyle = sunGlow;
        ctx.beginPath();
        ctx.arc(sx, sy, sunR * 2.4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = sunCore;
        ctx.globalAlpha = celestialVis;
        ctx.beginPath();
        ctx.arc(sx, sy, sunR, 0, 2 * Math.PI);
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // Moon — same arc, 18→30 (= 6) hours
        const adj = localHour < 6 ? localHour + 24 : localHour;
        const moonFrac = (adj - 18) / 12;
        const mx = moonFrac * w;
        const my = horizonY - Math.sin(moonFrac * Math.PI) * (horizonY - arcTop);
        const moonR = 11;
        const moonGlow = ctx.createRadialGradient(mx, my, 0, mx, my, moonR * 2.5);
        moonGlow.addColorStop(0,    `rgba(255, 250, 220, ${0.5 * celestialVis})`);
        moonGlow.addColorStop(1,    `rgba(255, 250, 220, 0)`);
        ctx.fillStyle = moonGlow;
        ctx.beginPath();
        ctx.arc(mx, my, moonR * 2.5, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#f3ecd4';
        ctx.globalAlpha = celestialVis;
        ctx.beginPath();
        ctx.arc(mx, my, moonR, 0, 2 * Math.PI);
        ctx.fill();
        // Crescent shadow
        ctx.fillStyle = mixColor(nightTop, '#000', 0.4);
        ctx.beginPath();
        ctx.arc(mx + 3.5, my - 1, moonR - 1, 0, 2 * Math.PI);
        ctx.fill();
        ctx.globalAlpha = 1;

        // Sprinkle of stars — fixed positions, twinkling alpha
        ctx.fillStyle = `rgba(247, 238, 216, 1)`;
        for (let i = 0; i < 22; i++) {
          const stx = ((i * 73) % w);
          const sty = ((i * 41) % (h * 0.35));
          const tw = 0.4 + 0.6 * Math.abs(Math.sin(t * 1.1 + i));
          ctx.globalAlpha = tw * 0.7 * celestialVis;
          ctx.fillRect(stx, sty, 1, 1);
        }
        ctx.globalAlpha = 1;
      }
    }

    // Horizon line
    ctx.strokeStyle = `rgba(255, 240, 200, ${0.22 - stormness * 0.18})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h * 0.4);
    ctx.lineTo(w, h * 0.4);
    ctx.stroke();

    // Cloud bank — soft radial puff that drifts L→R, denser in heavy weather.
    // Path the fill to an ellipse that matches the gradient extent so the
    // edges fade to transparent instead of clipping to a hard rectangle.
    const cloudSpeed = 6 + wind * 0.4;
    const cloudRx = 70 + wind * 2;
    const cloudRy = 18 + wind * 0.5;
    const cloudCx = ((t * cloudSpeed) % (w + cloudRx * 2 + 200)) - cloudRx - 100;
    const cloudCy = h * 0.22;
    const cloudOpacity = 0.18 + stormness * 0.45;
    const cloudGrad = ctx.createRadialGradient(cloudCx, cloudCy, 0, cloudCx, cloudCy, cloudRx);
    const cloudCore = stormness > 0.5 ? '40, 32, 28' : '247, 240, 218';
    cloudGrad.addColorStop(0, `rgba(${cloudCore}, ${cloudOpacity})`);
    cloudGrad.addColorStop(1, `rgba(${cloudCore}, 0)`);
    ctx.fillStyle = cloudGrad;
    ctx.save();
    // Squash the gradient circle vertically into a flat cloud shape
    ctx.translate(cloudCx, cloudCy);
    ctx.scale(1, cloudRy / cloudRx);
    ctx.beginPath();
    ctx.arc(0, 0, cloudRx, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();

    // Ocean wave layers — amplitude scales with wind
    const baseAmp = isDoldrums ? 0.3 : 1.4 + wind * 0.18;
    const baseSpeed = 14 + wind * 0.7;
    const layers = isDoldrums ? 2 : 6;
    for (let i = 0; i < layers; i++) {
      const y0 = h * 0.42 + i * 22;
      const speed = baseSpeed + i * 5;
      const amp = baseAmp + i * 0.5;
      const phase = t * speed * 0.06;
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.06 + i * 0.018})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y0);
      for (let x = 0; x <= w; x += 10) {
        const y = y0 + Math.sin((x + i * 30) * 0.045 + phase) * amp;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // The ship — bob and roll scale with sea state. List-to-port if hull is shot.
    const shipBaseY = h * 0.55;
    const bobAmp = isDoldrums ? 0.4 : 1.4 + wind * 0.18;
    const rollAmp = isDoldrums ? 0.005 : 0.04 + wind * 0.012;
    const bob = Math.sin(t * 0.9) * bobAmp;
    const roll = Math.sin(t * 0.7) * rollAmp + (1 - seaWeather.hullFrac) * 0.15;
    const sx = w / 2;
    const sy = shipBaseY + bob;

    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(roll);
    // Hull
    ctx.fillStyle = '#5e3f25';
    ctx.beginPath();
    ctx.moveTo(-8, 0); ctx.lineTo(8, 0); ctx.lineTo(6, 4); ctx.lineTo(-6, 4); ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#7a5230';
    ctx.fillRect(-7, 0, 14, 1);
    // Mast — falls with anchor / row sail level
    const mastDown = seaWeather.sailLevel === 'anchor';
    const mastHeight = mastDown ? 6 : 14;
    ctx.strokeStyle = '#3a2a18';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(0, -mastHeight);
    ctx.stroke();
    // Sail — present unless anchor / row, and reefed shows a smaller sail
    if (!mastDown && seaWeather.sailLevel !== 'row') {
      const sailH = seaWeather.sailLevel === 'reefed' ? 6
                  : seaWeather.sailLevel === 'half'   ? 9
                  : 11;
      const sailW = seaWeather.sailLevel === 'reefed' ? 3
                  : seaWeather.sailLevel === 'half'   ? 4.5
                  : 6;
      ctx.fillStyle = '#efe4cc';
      ctx.beginPath();
      ctx.moveTo(0, -mastHeight); ctx.lineTo(sailW, -mastHeight + sailH); ctx.lineTo(0, -mastHeight + sailH);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(58, 42, 24, 0.4)';
      ctx.stroke();
    }
    // Pennant (always)
    const pennantWave = Math.sin(t * 4) * 1.2;
    ctx.strokeStyle = '#c43c20';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(0, -mastHeight);
    ctx.lineTo(5 + pennantWave * 0.4, -mastHeight + 1 + pennantWave * 0.1);
    ctx.stroke();
    ctx.restore();

    // Wake under the ship — subtle when calm, white-cap spray when stormy,
    // and dramatic when a whale is towing the ship.
    const towing = seaWeather.whaling;
    const wakeAlpha = towing ? 0.55 : 0.18 + stormness * 0.4;
    ctx.fillStyle = `rgba(255, 255, 255, ${wakeAlpha})`;
    const wakeCount = towing ? 14 : isStorm ? 8 : 4;
    for (let i = 0; i < wakeCount; i++) {
      const wx = sx - 10 - i * 6;
      const wy = sy + 4 + Math.sin(t * 1.5 + i) * (0.6 + stormness * 1.6 + (towing ? 1.4 : 0));
      ctx.fillRect(wx, wy, 3, 1);
    }

    // ── Whaling: taut rope from the bow off into the foreground, whale tail churn
    if (towing) {
      // Rope (tense)
      ctx.strokeStyle = 'rgba(247, 238, 216, 0.65)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(sx + 7, sy - 1);            // ship bow
      ctx.lineTo(w + 30, h * 0.50 + Math.sin(t * 1.4) * 1.8); // off-canvas right
      ctx.stroke();
      // Spray bursts under the bow
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      for (let i = 0; i < 6; i++) {
        const px = sx + 6 + Math.random() * 10;
        const py = sy + 3 + Math.random() * 3;
        ctx.fillRect(px, py, 1.5, 1);
      }
      // Whale spout near the right edge — visible bursts every couple seconds
      const spoutCycle = (t % 3.2);
      if (spoutCycle < 0.6) {
        const spx = w - 18 + Math.sin(t * 0.7) * 3;
        const spy = h * 0.48;
        const a = 0.7 * (1 - spoutCycle / 0.6);
        ctx.fillStyle = `rgba(220, 230, 240, ${a})`;
        for (let i = 0; i < 5; i++) {
          ctx.fillRect(spx + Math.sin(i) * 2, spy - i * 2 - 2, 1.5, 2);
        }
      }
    }

    // ── Deployed nets — buoys bobbing astern of the ship
    if (seaWeather.netsDeployed > 0) {
      const baseY = sy + 6;
      for (let i = 0; i < Math.min(seaWeather.netsDeployed, 3); i++) {
        const bx = sx - 32 - i * 14;
        const by = baseY + Math.sin(t * 1.3 + i * 1.7) * 1.6;
        // Buoy
        ctx.fillStyle = '#c43c20';
        ctx.beginPath();
        ctx.arc(bx, by, 2.5, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#3a2a18';
        ctx.lineWidth = 1;
        ctx.stroke();
        // Faint net ripple
        ctx.strokeStyle = 'rgba(247, 238, 216, 0.18)';
        ctx.beginPath();
        ctx.arc(bx, by + 1, 6, 0, Math.PI, false);
        ctx.stroke();
      }
    }

    // ── Fishing rod — thin line from the rail to the water, with a bobber.
    //   Deadlines are wall-clock (epoch) from the captain state; `now` here
    //   is performance.now(), so compare with Date.now() instead.
    const wallNow = Date.now();
    if (wallNow < seaWeather.rodActiveUntil) {
      const rodX = sx + 12;
      const rodTopY = sy - 3;
      const bobberY = sy + 8 + Math.sin(t * 2.6) * 0.6;
      ctx.strokeStyle = 'rgba(58, 42, 24, 0.7)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(rodX, rodTopY);
      ctx.lineTo(rodX + 5, bobberY);
      ctx.stroke();
      // Small bobber
      ctx.fillStyle = '#c43c20';
      ctx.beginPath();
      ctx.arc(rodX + 5, bobberY, 1.2, 0, 2 * Math.PI);
      ctx.fill();
      // Rod (held by the crewman)
      ctx.strokeStyle = '#5e3f25';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(rodX - 2, sy - 4);
      ctx.lineTo(rodX, rodTopY);
      ctx.stroke();
    }

    // ── Crew swim — small heads bobbing in the water around the ship
    if (wallNow < seaWeather.swimUntil) {
      ctx.fillStyle = '#efe4cc';
      ctx.strokeStyle = '#3a2a18';
      ctx.lineWidth = 0.8;
      const swimmers = 5;
      for (let i = 0; i < swimmers; i++) {
        const offsetX = (i - (swimmers - 1) / 2) * 7 + Math.sin(t * 1.2 + i) * 1.5;
        const headX = sx + offsetX;
        const headY = sy + 7 + Math.cos(t * 1.4 + i * 1.3) * 0.6;
        ctx.beginPath();
        ctx.arc(headX, headY, 1.3, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
        // Tiny splash arcs
        ctx.strokeStyle = 'rgba(247, 238, 216, 0.5)';
        ctx.beginPath();
        ctx.arc(headX, headY + 1.2, 2.6, 0.2, Math.PI - 0.2);
        ctx.stroke();
        ctx.strokeStyle = '#3a2a18';
      }
    }

    // Rain particles — only in heavy weather. Use a persistent pool
    // and refill each frame so density tracks the wind.
    const rainTarget = isStorm ? 220 : isGale ? 60 : 0;
    while (rainDrops.length < rainTarget) {
      rainDrops.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: -40 - Math.random() * 60,   // wind-blown sideways
        vy: 200 + Math.random() * 200,
      });
    }
    while (rainDrops.length > rainTarget) rainDrops.pop();
    if (rainDrops.length > 0) {
      ctx.strokeStyle = isStorm ? 'rgba(180, 200, 220, 0.45)' : 'rgba(180, 200, 220, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const d of rainDrops) {
        d.x += d.vx * dt;
        d.y += d.vy * dt;
        if (d.y > h) { d.y = -5; d.x = Math.random() * (w + 100); }
        if (d.x < -10) d.x = w + 5;
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - d.vx * 0.012, d.y - d.vy * 0.012);
      }
      ctx.stroke();
    }

    // Lightning flash — only during a hurricane. Random at 4-12s intervals.
    if (seaWeather.hurricane) {
      if (now > nextLightningAt) {
        lightningFlash = 0.7;
        nextLightningAt = now + 4000 + Math.random() * 8000;
      }
      if (lightningFlash > 0) {
        ctx.fillStyle = `rgba(220, 220, 240, ${lightningFlash * 0.6})`;
        ctx.fillRect(0, 0, w, h * 0.42);
        // A jagged bolt at the start of a flash
        if (lightningFlash > 0.5) {
          ctx.strokeStyle = `rgba(255, 255, 255, ${lightningFlash})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          let lx = w * 0.2 + Math.random() * w * 0.6;
          let ly = 0;
          ctx.moveTo(lx, ly);
          while (ly < h * 0.35) {
            lx += (Math.random() - 0.5) * 30;
            ly += 8 + Math.random() * 10;
            ctx.lineTo(lx, ly);
          }
          ctx.stroke();
        }
        lightningFlash = Math.max(0, lightningFlash - dt * 1.6);
      }
    } else {
      lightningFlash = 0;
    }

    // Fog bank — gray overlay shrinks visibility from the edges in
    if (seaWeather.fogged) {
      const fogGrad = ctx.createRadialGradient(w / 2, h * 0.5, 30, w / 2, h * 0.5, w * 0.55);
      fogGrad.addColorStop(0,   'rgba(200, 205, 210, 0.0)');
      fogGrad.addColorStop(0.5, 'rgba(200, 205, 210, 0.45)');
      fogGrad.addColorStop(1,   'rgba(200, 205, 210, 0.85)');
      ctx.fillStyle = fogGrad;
      ctx.fillRect(0, 0, w, h);
    }
  };
  seaAnimHandle = requestAnimationFrame(draw);
}

/** Period-flavoured one-liner describing the current weather + wind, drawn under the ship canvas. */
function weatherFlavor(captain: CaptainSave, serverTime: number): string {
  const wind = captain.wind?.speedKnots ?? 0;
  const dir = captain.wind ? compassName(captain.wind.direction + Math.PI) : '—';
  const fogged = !!captain.foggedUntil && serverTime < captain.foggedUntil;
  const inHurricaneBelt = captain.ship
    ? captain.ship.position.x >= HURRICANE_BELT.x0 && captain.ship.position.x <= HURRICANE_BELT.x1 &&
      captain.ship.position.y >= HURRICANE_BELT.y0 && captain.ship.position.y <= HURRICANE_BELT.y1
    : false;
  const month = new Date(serverTime).getUTCMonth();
  const inHurricaneSeason = HURRICANE_SEASON_MONTHS.has(month) && inHurricaneBelt;

  let core: string;
  if (inHurricaneSeason && wind >= 32) {
    core = `Hurricane! The sky is black and the seas are mountainous. The men cling to the ropes, soaked through.`;
  } else if (wind >= 32) {
    core = `A storm out of the ${dir}. Sheets of rain. The masts groan and the bilge fills.`;
  } else if (wind >= 22) {
    core = `Gale rising from the ${dir} at ${wind.toFixed(1)} knots. Whitecaps as far as the eye can see.`;
  } else if (wind >= 14) {
    core = `A brisk wind from the ${dir} at ${wind.toFixed(1)} knots. The ship heels to leeward and runs hard.`;
  } else if (wind >= 7) {
    core = `A steady breeze from the ${dir} at ${wind.toFixed(1)} knots. The sea rolls in long swells.`;
  } else if (wind >= 2) {
    core = `A light air from the ${dir} at ${wind.toFixed(1)} knots. The sails barely take shape.`;
  } else {
    core = `Becalmed. Not a breath of wind, and the sails hang slack as wet linen.`;
  }
  if (fogged) core += ` A grey fog has rolled in — the lookout calls down the same fathoms over and over.`;
  return core;
}

/** Linearly mix two #rrggbb hex strings. Returns hex so the result can be
 *  chained back into another mixColor call. */
function mixColor(a: string, b: string, t: number): string {
  const ar = parseInt(a.slice(1, 3), 16), ag = parseInt(a.slice(3, 5), 16), ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16), bg = parseInt(b.slice(3, 5), 16), bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  const h = (v: number): string => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  return '#' + h(r) + h(g) + h(bl);
}

export function stopAtSeaAnimation(): void {
  stopSeaAnimation();
  closeMapModal();
}

// ── Compass dial
function drawCompass(
  canvas: HTMLCanvasElement,
  heading: number,
  wind: WindState | null,
  hasCompass: boolean,
  destinationBearing: number | null,
  destinationInitial: string,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) / 2 - 8;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#f7eed8';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
  ctx.fill();
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.strokeStyle = 'rgba(58, 42, 24, 0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 12, 0, 2 * Math.PI);
  ctx.stroke();

  for (let i = 0; i < 16; i++) {
    const angle = i * (Math.PI / 8) - Math.PI / 2;
    const isCardinal = i % 4 === 0;
    const isInter = i % 2 === 0;
    const tickLen = isCardinal ? 10 : isInter ? 6 : 4;
    const x1 = cx + Math.cos(angle) * (r - 12);
    const y1 = cy + Math.sin(angle) * (r - 12);
    const x2 = cx + Math.cos(angle) * (r - 12 - tickLen);
    const y2 = cy + Math.sin(angle) * (r - 12 - tickLen);
    ctx.strokeStyle = '#3a2a18';
    ctx.lineWidth = isCardinal ? 1.6 : 1;
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  ctx.fillStyle = '#3a2a18';
  ctx.font = 'bold 14px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const labels: [string, number, number][] = [
    ['N', cx, cy - r + 14],
    ['E', cx + r - 14, cy],
    ['S', cx, cy + r - 14],
    ['W', cx - r + 14, cy],
  ];
  for (const [t, x, y] of labels) ctx.fillText(t, x, y);

  ctx.fillStyle = 'rgba(58, 42, 24, 0.15)';
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
  ctx.fill();

  // Destination bearing marker — a gold tick on the outer rim, never moves except as the ship moves
  if (destinationBearing !== null) {
    const innerR = r - 4;
    const outerR = r + 4;
    const x1 = cx + Math.cos(destinationBearing) * innerR;
    const y1 = cy + Math.sin(destinationBearing) * innerR;
    const x2 = cx + Math.cos(destinationBearing) * outerR;
    const y2 = cy + Math.sin(destinationBearing) * outerR;
    ctx.strokeStyle = '#b8860b';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
    ctx.stroke();
    // Small star at the outer tip
    ctx.fillStyle = '#d9a72a';
    ctx.beginPath();
    ctx.arc(x2, y2, 3, 0, 2 * Math.PI);
    ctx.fill();
    // Destination initial inside the rim ("T" for Tortuga, "C" for Cádiz, …)
    const labelR = r - 22;
    const lx = cx + Math.cos(destinationBearing) * labelR;
    const ly = cy + Math.sin(destinationBearing) * labelR;
    ctx.fillStyle = '#b8860b';
    ctx.font = 'bold 10px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(destinationInitial, lx, ly);
  }

  if (wind && wind.speedKnots > 0.5) {
    const wlen = Math.min(r - 22, 18 + wind.speedKnots * 2);
    const wx = cx + Math.cos(wind.direction) * wlen;
    const wy = cy + Math.sin(wind.direction) * wlen;
    ctx.strokeStyle = '#3a5a82';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(wx, wy);
    ctx.stroke();
    const ang = wind.direction;
    const head = 6;
    ctx.fillStyle = '#3a5a82';
    ctx.beginPath();
    ctx.moveTo(wx, wy);
    ctx.lineTo(wx - Math.cos(ang - 0.4) * head, wy - Math.sin(ang - 0.4) * head);
    ctx.lineTo(wx - Math.cos(ang + 0.4) * head, wy - Math.sin(ang + 0.4) * head);
    ctx.closePath();
    ctx.fill();
  }

  if (hasCompass) {
    const nlen = r - 24;
    const hx = cx + Math.cos(heading) * nlen;
    const hy = cy + Math.sin(heading) * nlen;
    ctx.strokeStyle = '#8b3a1c';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy); ctx.lineTo(hx, hy);
    ctx.stroke();
    const head = 8;
    ctx.fillStyle = '#8b3a1c';
    ctx.beginPath();
    ctx.moveTo(hx, hy);
    ctx.lineTo(hx - Math.cos(heading - 0.4) * head, hy - Math.sin(heading - 0.4) * head);
    ctx.lineTo(hx - Math.cos(heading + 0.4) * head, hy - Math.sin(heading + 0.4) * head);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#3a2a18';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
    ctx.fill();
  } else {
    ctx.fillStyle = 'rgba(58, 42, 24, 0.5)';
    ctx.font = 'italic 11px Georgia, serif';
    ctx.fillText('no compass', cx, cy + r - 30);
  }
}

function bindCompassClick(canvas: HTMLCanvasElement, conn: Connection): void {
  canvas.onclick = (e: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const x = (e.clientX - rect.left) * (canvas.width / rect.width) - cx;
    const y = (e.clientY - rect.top) * (canvas.height / rect.height) - cy;
    let heading = Math.atan2(y, x);
    if (heading < 0) heading += 2 * Math.PI;
    // Snap to whole degrees so the heading remains a clean number; effectively continuous.
    const oneDeg = Math.PI / 180;
    heading = Math.round(heading / oneDeg) * oneDeg;
    if (heading >= 2 * Math.PI) heading -= 2 * Math.PI;
    conn.send({ type: 'set_heading', heading });
  };
}

const SAIL_LEVELS: Array<{ id: SailLevel; label: string; title: string; wide?: boolean }> = [
  { id: 'full',   label: 'Full',   title: 'Full sails — top speed, worst fishing' },
  { id: 'half',   label: 'Half',   title: 'Half sails — moderate speed and catch' },
  { id: 'reefed', label: 'Reefed', title: 'Reefed — barely moving, good fishing' },
  { id: 'anchor', label: 'Anchor', title: 'At anchor — best fishing, no sail speed' },
  { id: 'row',    label: 'Row',    title: 'Stow sails and pull at the oars — slow, costs morale, but works against any wind', wide: true },
];

function renderSailTrim(current: SailLevel): string {
  return `
    <div class="sail-trim" role="group" aria-label="Sail trim">
      <span class="sail-trim-label">Sails</span>
      <div class="sail-trim-buttons">
        ${SAIL_LEVELS.map(l => `
          <button
            class="sail-btn ${l.wide ? 'wide' : ''} ${current === l.id ? 'active' : ''}"
            data-sail-level="${l.id}"
            title="${esc(l.title)}"
            aria-pressed="${current === l.id ? 'true' : 'false'}"
          >${l.label}</button>
        `).join('')}
      </div>
    </div>
  `;
}

/** Convert a heading in radians to compass-style degrees where 0°=N, 90°=E, etc. */
function headingToDegN(rad: number): number {
  // Internal: 0=E, π/2=S, π=W, 3π/2=N. Compass UI: 0=N clockwise.
  let deg = (rad * 180 / Math.PI + 90) % 360;
  if (deg < 0) deg += 360;
  return deg;
}

function degNToHeading(deg: number): number {
  // Inverse of headingToDegN. Returns radians in [0, 2π) using internal 0=E convention.
  let rad = ((deg - 90) * Math.PI / 180) % (2 * Math.PI);
  if (rad < 0) rad += 2 * Math.PI;
  return rad;
}

// ── Sightings card
function renderSightingArrow(bearing: number): string {
  const deg = (bearing * 180 / Math.PI) + 90;
  return `<svg width="20" height="20" viewBox="0 0 20 20" style="transform: rotate(${deg}deg)" aria-hidden="true">
    <path d="M 10 2 L 14 12 L 10 9 L 6 12 Z" fill="var(--ink)"></path>
  </svg>`;
}

function renderSightings(
  sightings: Sighting[],
  captain: CaptainSave,
  serverTime: number,
  cannonReady: boolean,
  hasOutgoingTrade: boolean,
): string {
  if (sightings.length === 0) {
    return '<p class="card-empty">Nothing visible from the masthead.</p>';
  }
  const harpoonReady = serverTime >= captain.harpoonCooldownAt;
  const musketReady = serverTime >= captain.musketCooldownAt;
  const hasHarpoon = ownedOf(captain, 'harpoon') >= 1;
  const hasMusketKit = ownedOf(captain, 'musket') >= 1 && ownedOf(captain, 'musket_balls') >= 1 && ownedOf(captain, 'gunpowder') >= 1;
  const hasGrapples = ownedOf(captain, 'boarding_grapples') >= 1;
  return sightings.map(s => {
    const isWhale = s.entityKind === 'whale';
    const isShip = s.entityKind === 'ai_ship' || s.entityKind === 'pirate_ship';
    const isIsland = s.entityKind === 'island';
    const isCaptain = s.captainToken !== null;
    const hpInfo = isShip && s.hp !== null ? ` <span class="hp">HP ${s.hp.toFixed(0)}</span>` : '';
    const inRangeBadge = (isShip || isCaptain) && s.inCannonRange
      ? `<span class="in-range">in range</span>`
      : '';
    const flagBadge = isCaptain && s.flag
      ? `<span class="flag-badge flag-${esc(s.flag)}" title="${s.flag === 'war' ? 'flying the red flag' : 'flying the white flag'}">${s.flag === 'war' ? '⚔' : '⚐'} ${esc(s.flag)}</span>`
      : '';
    const tetherBadge = isWhale && s.tether
      ? (s.tetheredToMe
          ? `<span class="tether-badge mine">on your line · ${s.tether.harpoonsIn} harpoon${s.tether.harpoonsIn === 1 ? '' : 's'}</span>`
          : `<span class="tether-badge other">on another captain's line</span>`)
      : '';
    const fireBtn = (isShip || isCaptain) && s.inCannonRange && cannonReady
      ? isCaptain
        ? `<button class="danger" data-fire-captain="${esc(s.captainToken!)}">Fire</button>`
        : `<button class="danger" data-fire-entity="${s.entityId}">Fire</button>`
      : '';
    const tradeBtn = isCaptain && s.inTradeRange && !hasOutgoingTrade
      ? `<button class="secondary" data-trade-captain="${esc(s.captainToken!)}" data-trade-name="${esc(s.captainUsername ?? 'captain')}">Propose Trade</button>`
      : '';
    const boardBtn = isCaptain && s.inTradeRange && hasGrapples
      ? `<button class="danger" data-board-captain="${esc(s.captainToken!)}" data-board-name="${esc(s.captainUsername ?? 'captain')}">Board</button>`
      : '';
    const landBtn = isIsland && s.inLandingRange
      ? `<button class="primary" data-land="${s.entityId}">Land here</button>`
      : '';

    let whaleBtns = '';
    if (isWhale) {
      if (s.tetheredToMe && s.tether) {
        const exhausted = s.tether.fatigue >= 95;
        const closeForLance = s.distanceKm <= 1; // server enforces 0.5 km exactly
        if (exhausted && closeForLance) {
          whaleBtns += `<button class="primary" data-lance="${s.entityId}">Lance the whale</button>`;
        }
        if (hasHarpoon && harpoonReady) {
          whaleBtns += `<button class="danger" data-harpoon="${s.entityId}">Throw another harpoon</button>`;
        }
        if (hasMusketKit && musketReady) {
          whaleBtns += `<button class="danger" data-shoot-whale="${s.entityId}">Fire Musket</button>`;
        }
        whaleBtns += `<button class="secondary" data-cut-line="${s.entityId}">Cut line</button>`;
      } else if (!s.tether) {
        if (hasHarpoon && harpoonReady) {
          whaleBtns += `<button class="danger" data-harpoon="${s.entityId}">Harpoon</button>`;
        }
      }
    }

    return `
      <div class="sighting${s.tetheredToMe ? ' tethered-to-me' : ''}">
        <div class="bearing">${renderSightingArrow(s.bearing)}</div>
        <div class="info">
          <div class="label">${esc(s.label)}${hpInfo}${inRangeBadge}${flagBadge}${tetherBadge}</div>
          <div class="meta">${esc(compassName(s.bearing))} — about ${s.distanceKm} km</div>
        </div>
        <div class="actions">
          <button data-steer="${s.bearing.toFixed(4)}">Steer toward</button>
          ${whaleBtns}
          ${landBtn}
          ${tradeBtn}
          ${boardBtn}
          ${fireBtn}
        </div>
      </div>
    `;
  }).join('');
}

// ── Gunnery (shot type selector + cooldown indicator)
let selectedShotType: 'round' | 'grape' | 'chain' = 'round';

function renderGunnery(captain: CaptainSave, serverTime: number): string {
  const cannons6 = Math.floor(ownedOf(captain, 'cannon_6lb'));
  const cannons12 = Math.floor(ownedOf(captain, 'cannon_12lb'));
  const total = cannons6 + cannons12;
  if (total === 0) {
    return '<p class="gunnery-empty">No cannons aboard. You cannot return fire.</p>';
  }
  const powder = Math.floor(ownedOf(captain, 'gunpowder'));
  const round = Math.floor(ownedOf(captain, 'round_shot'));
  const grape = Math.floor(ownedOf(captain, 'grape_shot'));
  const chain = Math.floor(ownedOf(captain, 'chain_shot'));
  const cooldownSec = Math.ceil(Math.max(0, captain.cannonCooldownAt - serverTime) / 1000);
  const ready = cooldownSec === 0;
  const opts: Array<['round' | 'grape' | 'chain', string, number]> = [
    ['round', 'Round', round],
    ['grape', 'Grape', grape],
    ['chain', 'Chain', chain],
  ];

  return `
    <div class="gunnery">
      <div class="gunnery-info">
        <span><strong>${total}</strong> cannon${total === 1 ? '' : 's'} · <strong>${powder}</strong> powder</span>
        ${ready ? '<span class="gun-ready">Battery ready</span>'
                : `<span class="gun-cooling">Reloading — ${cooldownSec}s</span>`}
      </div>
      <div class="shot-selector">
        ${opts.map(([id, label, count]) => `
          <label class="${selectedShotType === id ? 'active' : ''} ${count < total ? 'short' : ''}">
            <input type="radio" name="shot-type" value="${id}" ${selectedShotType === id ? 'checked' : ''} />
            <span>${label}</span>
            <span class="count">${count}</span>
          </label>
        `).join('')}
      </div>
    </div>
  `;
}

// ── Chat card
function renderChatHistory(events: VoyageEvent[], embarkedAt: number | null): string {
  const chat = events.filter(e => e.kind === 'chat_in' || e.kind === 'chat_out');
  if (chat.length === 0) {
    return '<p class="card-empty">No signals exchanged.</p>';
  }
  // most recent first; show in reverse so latest at bottom
  return chat.slice(0, 30).reverse().map(e => `
    <div class="chat-line kind-${esc(e.kind)}">
      <span class="chat-time">${esc(formatEventTime(e.happenedAt, embarkedAt))}</span>
      <span class="chat-text">${esc(e.text)}</span>
    </div>
  `).join('');
}

// ── Fishing card
function renderFishing(captain: CaptainSave, serverTime: number): string {
  const nets = captain.deployedNets || [];
  const inv = (id: string) => ownedOf(captain, id);
  const rodReady = serverTime >= captain.rodCooldownAt;
  const harpoonReady = serverTime >= captain.harpoonCooldownAt;
  const rodCooldownSec = rodReady ? 0 : Math.ceil((captain.rodCooldownAt - serverTime) / 1000);
  const harpoonCooldownMin = harpoonReady ? 0 : Math.ceil((captain.harpoonCooldownAt - serverTime) / 60000);

  const rodBtn = inv('fishing_rod') < 1
    ? '<button disabled>No rod</button>'
    : rodReady
      ? '<button id="cast-rod-btn">Cast Rod</button>'
      : `<button disabled>Rod ready in ${rodCooldownSec}s</button>`;

  const deployBtn = inv('fishing_net') < 1
    ? '<button class="secondary" disabled>No nets to deploy</button>'
    : '<button class="secondary" id="deploy-net-btn">Deploy a Net</button>';

  const harpoonStatus = inv('harpoon') < 1
    ? '<span class="muted">No harpoons aboard.</span>'
    : harpoonReady
      ? `<span class="muted">${inv('harpoon')} harpoon${inv('harpoon') > 1 ? 's' : ''} ready. Pick a whale from sightings.</span>`
      : `<span class="muted">${inv('harpoon')} harpoon${inv('harpoon') > 1 ? 's' : ''} aboard. Next cast in ${harpoonCooldownMin} min.</span>`;

  let netsHtml = '';
  if (nets.length > 0) {
    netsHtml = nets.map(n => `
      <div class="deployed-net ${n.broken ? 'broken' : ''}">
        <div>
          <div class="net-label">Net #${n.id}${n.broken ? ' — torn' : ''}</div>
          <div class="net-meta">Deployed ${esc(netAge(n.deployedAt, serverTime))} ago, ${n.accumulatedFish.toFixed(1)} fish in the cod</div>
        </div>
        <button class="secondary" data-haul-net="${n.id}">Haul</button>
      </div>
    `).join('');
  }

  return `
    <div class="fishing-actions">
      ${rodBtn}
      ${deployBtn}
    </div>
    <div class="fishing-status">${harpoonStatus}</div>
    ${netsHtml}
    <div class="fishing-summary">
      <span>Fish caught: <strong>${inv('fish_caught').toFixed(0)}</strong></span>
      <span>Whale meat: <strong>${inv('whale_meat').toFixed(0)}</strong></span>
      <span>Bait: <strong>${inv('bait').toFixed(0)}</strong></span>
      <span>Spare netting: <strong>${inv('spare_netting').toFixed(0)}</strong></span>
    </div>
  `;
}

// ── Trade modal — inventory <select>
function renderInventorySelect(captain: CaptainSave): string {
  const excluded = new Set(['sailor', 'ship_surgeon', 'master_gunner']);
  const options = captain.inventory
    .filter(i => !excluded.has(i.itemId) && i.quantity >= 1)
    .map(i => `<option value="${esc(i.itemId)}">${esc(i.itemId)} (× ${Math.floor(i.quantity)})</option>`)
    .join('');
  return options || '<option value="">— no goods to trade —</option>';
}

// ── Crew + Brig cards
function crewBadge(c: CrewMember): string {
  let out = '';
  if (c.crime === 'murder') out += `<span class="crew-badge bad" title="Suspected of murder">murder</span>`;
  else if (c.crime === 'theft') out += `<span class="crew-badge warn" title="Suspected of theft">theft</span>`;
  if (c.syphilis >= SYPHILIS_METER_MAX) out += `<span class="crew-badge mad" title="Syphilitic insanity — beyond all help">mad</span>`;
  else if (c.syphilis > 0) out += `<span class="crew-badge pox" title="The French disease — the surgeon can only watch">pox</span>`;
  return out;
}

/** The syphilis meter — shown only once a man is infected. Fills over ~a month. */
function poxMeter(c: CrewMember): string {
  if (c.syphilis <= 0) return '';
  const pct = Math.max(2, Math.min(100, Math.round((c.syphilis / SYPHILIS_METER_MAX) * 100)));
  const mad = c.syphilis >= SYPHILIS_METER_MAX;
  return `<span class="pox-meter" title="${mad ? 'Syphilitic insanity' : `The pox advances — ${pct}%`}"><span class="pox-fill${mad ? ' mad' : ''}" style="width:${pct}%"></span></span>`;
}

function renderCrewCard(captain: CaptainSave, serverTime: number, locked: boolean): string {
  const active = captain.crew.filter(c => c.state === 'active');
  const rumQty = captain.inventory.find(i => i.itemId === 'rum')?.quantity ?? 0;
  const atAnchor = captain.ship?.sailLevel === 'anchor';

  const rumReady = serverTime >= captain.rumRationCooldownAt;
  const swimReady = serverTime >= captain.swimCooldownAt;
  const rumCdMin = rumReady ? 0 : Math.ceil((captain.rumRationCooldownAt - serverTime) / 60_000);
  const swimCdMin = swimReady ? 0 : Math.ceil((captain.swimCooldownAt - serverTime) / 60_000);

  const rumDisabled = locked || rumQty < 1 || !rumReady;
  const rumLabel = rumQty < 1
    ? 'No rum'
    : rumReady ? `Ration Rum (1 cask)` : `Ration Rum (ready in ${rumCdMin}m)`;

  const swimDisabled = locked || !atAnchor || !swimReady;
  const swimLabel = !atAnchor
    ? 'Crew Swim (drop anchor first)'
    : swimReady ? 'Crew Swim' : `Crew Swim (ready in ${swimCdMin}m)`;

  if (active.length === 0 && captain.crew.length === 0) {
    return '<p class="card-empty">No crew aboard.</p>';
  }

  const rows = active.length === 0
    ? '<p class="card-empty">No men above decks.</p>'
    : active.map(c => `
        <div class="crew-row ${c.crime !== 'none' ? 'wanted' : ''}${c.syphilis >= SYPHILIS_METER_MAX ? ' insane' : ''}">
          <span class="crew-name">${esc(c.name)}</span>
          ${poxMeter(c)}
          <span class="crew-tags">${crewBadge(c)}</span>
          ${locked ? '' : `<button class="danger" data-imprison="${c.id}">Brig</button>`}
        </div>
      `).join('');

  return `
    <div class="crew-actions">
      <button class="secondary" id="ration-rum-btn" ${rumDisabled ? 'disabled' : ''}>${esc(rumLabel)}</button>
      <button class="secondary" id="send-swim-btn" ${swimDisabled ? 'disabled' : ''}>${esc(swimLabel)}</button>
    </div>
    <div class="crew-list">${rows}</div>
  `;
}

function renderBrigCard(captain: CaptainSave, locked: boolean): string {
  const inBrig: Array<CrewMember | null> = new Array(BRIG_CAPACITY).fill(null);
  for (const c of captain.crew) {
    if (c.state === 'in_brig' && c.brigCellIndex !== null && c.brigCellIndex >= 0 && c.brigCellIndex < BRIG_CAPACITY) {
      inBrig[c.brigCellIndex] = c;
    }
  }
  const cells = inBrig.map((c, i) => {
    if (!c) {
      return `
        <div class="brig-cell empty">
          <div class="brig-bars"></div>
          <div class="brig-empty-label">Cell ${i + 1} — empty</div>
        </div>
      `;
    }
    const crimeLabel = c.crime === 'murder' ? 'Murder' : c.crime === 'theft' ? 'Theft' : 'No charge';
    const status = c.rehabilitated ? '<span class="brig-rehab" title="The bosun reckons he\'s come round">rehabilitated</span>' : '';
    return `
      <div class="brig-cell occupied ${c.rehabilitated ? 'rehab' : ''}">
        <div class="brig-bars"></div>
        <div class="prisoner-figure" aria-hidden="true">☹</div>
        <div class="prisoner-info">
          <div class="prisoner-name">${esc(c.name)}</div>
          <div class="prisoner-crime">${esc(crimeLabel)} ${status}</div>
          ${poxMeter(c)}
        </div>
        ${locked ? '' : `
          <div class="prisoner-actions">
            <button class="secondary" data-talk="${c.id}">Talk</button>
            <button data-release="${c.id}">Release</button>
            <button class="danger" data-plank="${c.id}" data-plank-name="${esc(c.name)}">Plank</button>
          </div>
        `}
      </div>
    `;
  }).join('');
  return `<div class="brig-cells">${cells}</div>`;
}

// ── Voyage log (non-chat events)
function renderLog(events: VoyageEvent[], embarkedAt: number | null): string {
  const filtered = events.filter(e => e.kind !== 'chat_in' && e.kind !== 'chat_out');
  if (filtered.length === 0) {
    return '<p class="log-empty">No entries yet.</p>';
  }
  return filtered.map(ev => `
    <div class="log-entry kind-${esc(ev.kind)}">
      <span class="log-time">${esc(formatEventTime(ev.happenedAt, embarkedAt))}</span>
      <span class="log-text">${esc(ev.text)}</span>
    </div>
  `).join('');
}

// ── Main render
export function renderAtSeaScreen(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
  serverTime: number,
  fog: Set<string>,
  memorials: WreckMemorial[],
  hurricanes: HurricaneSnapshot[] = [],
  fogBanks: FogBankSnapshot[] = [],
): void {
  // Preserve in-progress chat text across server-driven re-renders
  const savedChatText = container.querySelector<HTMLInputElement>('#chat-input')?.value ?? '';

  const origin = captain.originId ? getOrigin(captain.originId) : null;
  const ship = captain.ship!;
  const wind = captain.wind;
  const embarked = captain.embarkedAt ?? serverTime;
  const elapsed = Math.max(0, serverTime - embarked);

  const sailors = ownedOf(captain, 'sailor');
  const cannons = ownedOf(captain, 'cannon_6lb') + ownedOf(captain, 'cannon_12lb');
  const hasTelescope = ownedOf(captain, 'telescope') > 0;
  const hasCompass = ownedOf(captain, 'compass') > 0;
  const hasSurgeon = ownedOf(captain, 'ship_surgeon') > 0;
  const hasGunner = ownedOf(captain, 'master_gunner') > 0;
  const sailsDamaged = captain.sailsDamagedUntil > serverTime;
  const sailclothQty = ownedOf(captain, 'spare_sailcloth');
  const hullPct = ship.hullIntegrity;
  const lumberQty = ownedOf(captain, 'lumber');
  const carpenterQty = ownedOf(captain, 'carpenters_tools');

  const headingName = hasCompass ? compassName(ship.heading) : '???';
  const windFromName = wind ? compassName(wind.direction + Math.PI) : '—';

  const isStatusBad = captain.status === 'lost';
  const isStatusArrived = captain.status === 'arrived';
  const locked = isStatusBad || isStatusArrived;
  const destinationName = (captain.destinationId ? getPort(captain.destinationId)?.name : null) ?? 'Tortuga';

  setHTML(container, `
    <div class="at-sea">
      <h1 class="title">${isStatusArrived ? esc(destinationName) : isStatusBad ? 'Lost at Sea' : 'At Sea'}</h1>
      <p class="subtitle">Captain ${esc(captain.username)}${origin ? ` — ${esc(origin.name)}` : ''}</p>

      <canvas class="sea-canvas" width="640" height="200"></canvas>
      <p class="sea-flavor">${esc(weatherFlavor(captain, serverTime))}</p>
      ${captain.coastInSight ? `
        <p class="coast-banner">
          Coast in sight to the <strong>${esc(compassName(captain.coastInSight.bearing))}</strong> —
          ${esc(captain.coastInSight.name)}, about ${captain.coastInSight.distanceKm} km.
        </p>` : ''}

      <div class="flag-bar">
        <span class="flag-current flag-${esc(captain.flag)}">${captain.flag === 'war' ? '⚔ flying the red flag' : '⚐ flying the white flag of peace'}</span>
        ${locked ? '' : `<button class="secondary" id="flag-toggle-btn">${captain.flag === 'war' ? 'Lower to peace' : 'Hoist for war'}</button>`}
      </div>

      ${captain.incomingTrade ? `
        <div class="trade-banner incoming">
          <div>
            <strong>${esc(captain.incomingTrade.senderUsername)}</strong> offers
            <strong>${captain.incomingTrade.quantity}</strong> ${esc(captain.incomingTrade.itemName)}
            for <strong>${captain.incomingTrade.priceReales}</strong> reales.
          </div>
          <div class="actions">
            <button class="primary" data-trade-accept="${captain.incomingTrade.id}">Accept</button>
            <button class="danger" data-trade-decline="${captain.incomingTrade.id}">Decline</button>
          </div>
        </div>` : ''}
      ${captain.outgoingTrade ? `
        <div class="trade-banner outgoing">
          <div>
            Waiting for response — you offered <strong>${captain.outgoingTrade.quantity}</strong>
            ${esc(captain.outgoingTrade.itemName)} for
            <strong>${captain.outgoingTrade.priceReales}</strong> reales.
          </div>
          <div class="actions">
            <button class="secondary" data-trade-cancel="${captain.outgoingTrade.id}">Withdraw</button>
          </div>
        </div>` : ''}

      ${(() => {
        const loose = captain.crew.filter(c => c.state === 'active' && c.crime !== 'none' && !c.rehabilitated);
        if (locked || loose.length === 0) return '';
        const names = loose.map(c => `<strong>${esc(c.name)}</strong>${c.crime === 'murder' ? ' (murder)' : ' (theft)'}`).join(', ');
        return `
          <div class="crime-banner">
            <div>
              <strong>Crime aboard</strong> — ${names} ${loose.length === 1 ? 'walks' : 'walk'} free.
              Visit the Crew below to put ${loose.length === 1 ? 'him' : 'them'} in irons.
            </div>
          </div>`;
      })()}

      ${(() => {
        const towed = (captain.sightings || []).find(s => s.tetheredToMe && s.entityKind === 'whale');
        if (!towed || !towed.tether || locked) return '';
        const f = towed.tether.fatigue;
        const exhausted = f >= 95;
        return `
          <div class="tether-banner">
            <div class="tether-info">
              <strong>Whale on the line</strong> —
              ${exhausted
                ? 'she lies winded alongside. Close to within half a kilometre and finish her with the lance.'
                : `the rope sings as she runs. ${towed.tether.harpoonsIn} harpoon${towed.tether.harpoonsIn === 1 ? '' : 's'} in.`}
              <div class="tether-bar"><div class="tether-fill" style="width:${Math.min(100, f).toFixed(1)}%"></div></div>
              <div class="tether-meta">Fatigue ${f.toFixed(0)}% · tow ${captain.ship?.speedKnots.toFixed(1) ?? '0'} kt</div>
            </div>
          </div>`;
      })()}
      ${sailsDamaged && sailclothQty >= 1 && !locked ? `
        <div class="repair-banner">
          <span>Sails are torn — speed reduced. ${sailclothQty} bolt${sailclothQty !== 1 ? 's' : ''} of sailcloth in the hold.</span>
          <button class="primary" id="repair-sails-btn">Repair Sails</button>
        </div>` : ''}
      ${hullPct < 100 && !locked ? `
        <div class="repair-banner">
          <span>
            Hull at ${hullPct.toFixed(0)}%${lumberQty > 0 ? ` — ${lumberQty} bundle${lumberQty !== 1 ? 's' : ''} of lumber & nails in the hold.`
              : ' — no lumber aboard, you must wait for the carpenter.'}
          </span>
          <button class="primary" id="repair-hull-btn"
            ${(lumberQty < 1 || carpenterQty < 1) ? 'disabled' : ''}>
            Repair Hull
          </button>
        </div>` : ''}

      <div class="atsea-grid">
        <div class="compass-panel card">
          <canvas class="compass" width="220" height="220" ${locked ? 'data-locked="true"' : ''}></canvas>
          <p class="compass-hint">
            ${isStatusBad ? 'The helm answers no longer.'
              : isStatusArrived ? 'You have arrived. Drop anchor.'
              : hasCompass ? 'Click the dial to set a new heading.'
              : 'You have no compass; you sail blind.'}
          </p>
          ${!locked && hasCompass ? `
            <div class="heading-input">
              <label for="heading-deg-input">Heading</label>
              <input type="number" id="heading-deg-input" min="0" max="359" step="1" value="${Math.round(headingToDegN(ship.heading))}" />
              <span>°</span>
              <button id="heading-deg-set">Set</button>
            </div>` : ''}
          ${locked ? '' : renderSailTrim(ship.sailLevel)}
          ${locked ? '' : `<button class="map-btn" id="open-map-btn">Open Chart</button>`}
          ${locked ? '' : `<button class="howto-link" id="howto-link-btn">How to Play</button>`}
          ${isStatusBad ? `
            <p class="compass-hint" style="margin-top: 14px;">The sea keeps what it takes. The ledger is closed.</p>
            <button class="primary" id="retire-btn" style="margin-top: 6px;">Return to Main Menu</button>` : ''}
        </div>

        <div class="state-panel card">
          <div class="state-log">
            <div><span class="key">Time at sea</span><span class="val">${esc(formatElapsed(elapsed))}</span></div>
            <div><span class="key">Position</span><span class="val">${Math.abs(ship.position.x).toFixed(1)} km ${ship.position.x < 0 ? 'W' : 'E'}, ${Math.abs(ship.position.y).toFixed(1)} km ${ship.position.y < 0 ? 'N' : 'S'}</span></div>
            <div><span class="key">Heading</span><span class="val">${esc(headingName)}</span></div>
            <div><span class="key">Speed</span><span class="val">${ship.speedKnots.toFixed(1)} knots</span></div>
            <div><span class="key">Wind</span><span class="val">${wind ? `${wind.speedKnots.toFixed(1)} kt from ${esc(windFromName)}` : '—'}</span></div>
            <div><span class="key">Hull</span><span class="val">${ship.hullIntegrity.toFixed(0)}%</span></div>
            <div><span class="key">Crew</span><span class="val">${sailors}${hasSurgeon ? ', surgeon' : ''}${hasGunner ? ', gunner' : ''}</span></div>
            <div><span class="key">Stores</span><span class="val">${formatStores(captain)}</span></div>
            <div><span class="key">Antiscurvy</span><span class="val">${formatAntiscurvy(captain)}</span></div>
            <div><span class="key">Armaments</span><span class="val">${cannons} cannon${cannons !== 1 ? 's' : ''}</span></div>
            <div><span class="key">Watch</span><span class="val">${hasTelescope ? 'telescope' : 'naked eye'}</span></div>
            <div><span class="key">Morale</span><span class="val morale-${moraleClass(captain.morale)}">${renderMorale(captain.morale)}</span></div>
          </div>
        </div>
      </div>

      <section class="card sightings-card">
        <h2>Sightings <span class="lede">— what the lookout reports</span></h2>
        ${renderGunnery(captain, serverTime)}
        <div class="sightings-list">${renderSightings(captain.sightings || [], captain, serverTime, serverTime >= captain.cannonCooldownAt, !!captain.outgoingTrade)}</div>
      </section>

      <div class="atsea-grid-2">
        <section class="card chat-card">
          <h2>Signals <span class="lede">— hails sent and received</span></h2>
          <div class="chat-history">${renderChatHistory(captain.voyageLog, captain.embarkedAt)}</div>
          ${locked ? '' : `
            <div class="chat-input-row">
              <input type="text" id="chat-input" placeholder="Send a signal..." maxlength="200" />
              <button id="chat-send-btn">Send</button>
            </div>
          `}
        </section>

        <section class="card fishing-card">
          <h2>Fishing <span class="lede">— line, net, harpoon</span></h2>
          ${renderFishing(captain, serverTime)}
        </section>
      </div>

      <div class="atsea-grid-2">
        <section class="card crew-card">
          <h2>Crew <span class="lede">— the men under your command</span></h2>
          ${renderCrewCard(captain, serverTime, locked)}
        </section>

        <section class="card brig-card">
          <h2>Brig <span class="lede">— three iron cells below the waterline</span></h2>
          ${renderBrigCard(captain, locked)}
        </section>
      </div>

      <section class="card voyage-log">
        <h2>Captain's Log <span class="lede">— the ship's record</span></h2>
        <div class="log-list">${renderLog(captain.voyageLog, captain.embarkedAt)}</div>
      </section>
    </div>

    <div id="trade-modal" class="trade-modal" hidden>
      <div class="trade-modal-backdrop"></div>
      <div class="trade-modal-panel">
        <h3>Propose a Trade</h3>
        <p>To <strong id="trade-target-name">captain</strong></p>
        <label>What you're offering</label>
        <select id="trade-item-select">${renderInventorySelect(captain)}</select>
        <label>Quantity</label>
        <input type="number" id="trade-qty" min="1" value="1" />
        <label>Asking price (reales)</label>
        <input type="number" id="trade-price" min="0" value="100" />
        <div class="trade-modal-actions">
          <button class="secondary" id="trade-modal-cancel">Cancel</button>
          <button class="primary" id="trade-modal-send">Send Offer</button>
        </div>
      </div>
    </div>
  `);

  // Sea animation — feed it the live weather state so the ship's window
  // shows the conditions: heavy rain in storms, lightning in hurricanes,
  // fog overlay when fogged in, mast down at anchor, and so on.
  const seaCanvas = container.querySelector<HTMLCanvasElement>('canvas.sea-canvas');
  if (seaCanvas) {
    const inHurricaneBelt =
      ship.position.x >= HURRICANE_BELT.x0 && ship.position.x <= HURRICANE_BELT.x1 &&
      ship.position.y >= HURRICANE_BELT.y0 && ship.position.y <= HURRICANE_BELT.y1;
    const month = new Date(serverTime).getUTCMonth();
    const inHurricaneSeason = HURRICANE_SEASON_MONTHS.has(month) && inHurricaneBelt;
    // Local time at the ship's longitude. 1 hour of timezone ≈ 1440 km west at
    // these latitudes; the X axis is negative going west, so the offset is
    // simply position.x / 1440 hours.
    const d = new Date(serverTime);
    const utcHour = d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600;
    const localHour = ((utcHour + ship.position.x / 1440) % 24 + 24) % 24;
    // Anything tethered to us flips the sea into "sleigh ride" mode.
    const onTheLine = (captain.sightings || []).some(s => s.tetheredToMe && s.entityKind === 'whale');
    // Rod / swim animations bracket the moment the action was taken — derive
    // from each cooldown's end so we don't need extra state from the server.
    const ROD_COOLDOWN_MS = 2 * 60_000;
    const ROD_ANIM_MS = 5_000;
    const rodActiveUntil = captain.rodCooldownAt > 0
      ? captain.rodCooldownAt - ROD_COOLDOWN_MS + ROD_ANIM_MS
      : 0;
    const SWIM_COOLDOWN_MS = 4 * 3600_000;
    const SWIM_ANIM_MS = 4 * 60_000;
    const swimUntil = captain.swimCooldownAt > 0
      ? captain.swimCooldownAt - SWIM_COOLDOWN_MS + SWIM_ANIM_MS
      : 0;
    setSeaWeather({
      windSpeedKnots: wind?.speedKnots ?? 0,
      windDirRad: wind?.direction ?? 0,
      fogged: !!captain.foggedUntil && serverTime < captain.foggedUntil,
      hurricane: inHurricaneSeason,
      sailLevel: ship.sailLevel,
      hullFrac: Math.max(0, Math.min(1, ship.hullIntegrity / 100)),
      localHour,
      whaling: onTheLine,
      rodActiveUntil,
      netsDeployed: (captain.deployedNets || []).filter(n => !n.broken).length,
      swimUntil,
    });
    startSeaAnimation(seaCanvas);
  }

  // Compass
  const compass = container.querySelector<HTMLCanvasElement>('canvas.compass');
  if (compass) {
    drawCompass(compass, ship.heading, wind, hasCompass, captain.destinationBearing, destinationName.charAt(0).toUpperCase());
    if (!locked) bindCompassClick(compass, conn);
    else compass.style.cursor = 'default';
  }

  // Sighting actions
  container.querySelectorAll<HTMLButtonElement>('[data-steer]').forEach(btn => {
    btn.onclick = () => {
      const bearing = parseFloat(btn.dataset.steer!);
      if (Number.isFinite(bearing)) conn.send({ type: 'set_heading', heading: bearing });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-harpoon]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.harpoon!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'throw_harpoon', entityId: id });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-lance]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.lance!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'lance_whale', entityId: id });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-shoot-whale]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.shootWhale!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'shoot_whale', entityId: id });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-cut-line]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.cutLine!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'cut_line', entityId: id });
    };
  });

  const currentShot = (): 'round' | 'grape' | 'chain' => {
    const checked = container.querySelector<HTMLInputElement>('input[name="shot-type"]:checked');
    const v = checked?.value;
    return v === 'grape' || v === 'chain' ? v : 'round';
  };

  // Shot type selector — update active label highlight on change
  container.querySelectorAll<HTMLInputElement>('input[name="shot-type"]').forEach(inp => {
    inp.onchange = () => {
      if (!inp.checked) return;
      selectedShotType = currentShot();
      container.querySelectorAll<HTMLLabelElement>('.shot-selector label').forEach(l => {
        l.classList.toggle('active', l.querySelector<HTMLInputElement>('input')?.value === selectedShotType);
      });
    };
  });

  // Fire buttons — read shot type from the DOM at click time
  container.querySelectorAll<HTMLButtonElement>('[data-fire-captain]').forEach(btn => {
    btn.onclick = () => conn.send({ type: 'fire', targetCaptainToken: btn.dataset.fireCaptain!, shotType: currentShot() });
  });
  container.querySelectorAll<HTMLButtonElement>('[data-fire-entity]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.fireEntity!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'fire', targetEntityId: id, shotType: currentShot() });
    };
  });


  // Fishing actions
  const castBtn = container.querySelector<HTMLButtonElement>('#cast-rod-btn');
  if (castBtn) castBtn.onclick = () => conn.send({ type: 'cast_rod' });
  const deployBtn = container.querySelector<HTMLButtonElement>('#deploy-net-btn');
  if (deployBtn) deployBtn.onclick = () => conn.send({ type: 'deploy_net' });
  container.querySelectorAll<HTMLButtonElement>('[data-haul-net]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.haulNet!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'haul_net', netId: id });
    };
  });

  // Chat send
  const chatInput = container.querySelector<HTMLInputElement>('#chat-input');
  if (chatInput && savedChatText) chatInput.value = savedChatText;
  const chatBtn = container.querySelector<HTMLButtonElement>('#chat-send-btn');
  if (chatInput && chatBtn) {
    const send = (): void => {
      const text = chatInput.value.trim();
      if (!text) return;
      conn.send({ type: 'chat', text });
      chatInput.value = '';
    };
    chatBtn.onclick = send;
    chatInput.onkeydown = (e) => { if (e.key === 'Enter') send(); };
  }

  // Flag toggle
  const flagBtn = container.querySelector<HTMLButtonElement>('#flag-toggle-btn');
  if (flagBtn) {
    flagBtn.onclick = () => {
      conn.send({ type: 'set_flag', flag: captain.flag === 'war' ? 'peace' : 'war' });
    };
  }

  // Heading input
  const headingInput = container.querySelector<HTMLInputElement>('#heading-deg-input');
  const headingSetBtn = container.querySelector<HTMLButtonElement>('#heading-deg-set');
  if (headingInput && headingSetBtn) {
    const apply = (): void => {
      const v = parseInt(headingInput.value, 10);
      if (!Number.isFinite(v)) return;
      const deg = ((v % 360) + 360) % 360;
      conn.send({ type: 'set_heading', heading: degNToHeading(deg) });
    };
    headingSetBtn.onclick = apply;
    headingInput.onkeydown = (e) => { if (e.key === 'Enter') apply(); };
  }

  // Sail trim
  container.querySelectorAll<HTMLButtonElement>('[data-sail-level]').forEach(btn => {
    btn.onclick = () => {
      const level = btn.dataset.sailLevel as SailLevel | undefined;
      if (level && level !== ship.sailLevel) conn.send({ type: 'trim_sails', level });
    };
  });

  // Open map
  const openMapBtn = container.querySelector<HTMLButtonElement>('#open-map-btn');
  if (openMapBtn) openMapBtn.onclick = () => openMapModal(captain, fog, memorials, hurricanes, fogBanks);
  const howtoLinkBtn = container.querySelector<HTMLButtonElement>('#howto-link-btn');
  if (howtoLinkBtn) howtoLinkBtn.onclick = openHowToPlay;
  // Push the latest captain state + fog + weather into the map if it's already open
  refreshMapIfOpen(captain, fog, memorials, hurricanes, fogBanks);

  // Lost at sea — the only thing left to do is strike the name from the register.
  const retireBtn = container.querySelector<HTMLButtonElement>('#retire-btn');
  if (retireBtn) retireBtn.onclick = () => {
    const ok = confirm(`Return to the main menu?

This erases Captain ${captain.username}'s save — the name will be free for a new voyage.`);
    if (ok) conn.send({ type: 'retire' });
  };

  // Repair sails
  const repairSailsBtn = container.querySelector<HTMLButtonElement>('#repair-sails-btn');
  if (repairSailsBtn) repairSailsBtn.onclick = () => conn.send({ type: 'repair_sails' });
  const repairHullBtn = container.querySelector<HTMLButtonElement>('#repair-hull-btn');
  if (repairHullBtn) repairHullBtn.onclick = () => conn.send({ type: 'repair_hull' });

  // Crew actions
  const rumBtn = container.querySelector<HTMLButtonElement>('#ration-rum-btn');
  if (rumBtn) rumBtn.onclick = () => conn.send({ type: 'ration_rum' });
  const swimBtn = container.querySelector<HTMLButtonElement>('#send-swim-btn');
  if (swimBtn) swimBtn.onclick = () => conn.send({ type: 'send_swim' });
  container.querySelectorAll<HTMLButtonElement>('[data-imprison]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.imprison!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'imprison', crewId: id });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-release]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.release!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'release_from_brig', crewId: id });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-talk]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.talk!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'talk_to_prisoner', crewId: id });
    };
  });
  // The plank — fatal and costly, gated by a confirm.
  container.querySelectorAll<HTMLButtonElement>('[data-plank]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.plank!, 10);
      if (!Number.isFinite(id)) return;
      const name = btn.dataset.plankName || 'this man';
      const ok = window.confirm(
        `Send ${name} over the side? There is no coming back from the plank — `
        + `and the men will not forget it. Morale will be cut in half.`,
      );
      if (ok) conn.send({ type: 'walk_plank', crewId: id });
    };
  });

  // Landing
  container.querySelectorAll<HTMLButtonElement>('[data-land]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.land!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'land', entityId: id });
    };
  });

  // Trade responses
  container.querySelectorAll<HTMLButtonElement>('[data-trade-accept]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.tradeAccept!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'respond_trade', tradeId: id, accept: true });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-trade-decline]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.tradeDecline!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'respond_trade', tradeId: id, accept: false });
    };
  });
  container.querySelectorAll<HTMLButtonElement>('[data-trade-cancel]').forEach(btn => {
    btn.onclick = () => {
      const id = parseInt(btn.dataset.tradeCancel!, 10);
      if (Number.isFinite(id)) conn.send({ type: 'cancel_trade', tradeId: id });
    };
  });

  // Trade modal — open when "Propose Trade" is clicked
  const tradeModal = container.querySelector<HTMLElement>('#trade-modal');
  const tradeItemSelect = container.querySelector<HTMLSelectElement>('#trade-item-select');
  const tradeQty = container.querySelector<HTMLInputElement>('#trade-qty');
  const tradePrice = container.querySelector<HTMLInputElement>('#trade-price');
  const tradeNameSpan = container.querySelector<HTMLElement>('#trade-target-name');
  const tradeCancel = container.querySelector<HTMLButtonElement>('#trade-modal-cancel');
  const tradeSend = container.querySelector<HTMLButtonElement>('#trade-modal-send');
  let tradeTargetToken: string | null = null;

  const closeTradeModal = (): void => { if (tradeModal) tradeModal.hidden = true; };
  if (tradeCancel) tradeCancel.onclick = closeTradeModal;
  if (tradeModal) {
    const backdrop = tradeModal.querySelector<HTMLElement>('.trade-modal-backdrop');
    if (backdrop) backdrop.onclick = closeTradeModal;
  }

  container.querySelectorAll<HTMLButtonElement>('[data-trade-captain]').forEach(btn => {
    btn.onclick = () => {
      tradeTargetToken = btn.dataset.tradeCaptain!;
      if (tradeNameSpan) tradeNameSpan.textContent = btn.dataset.tradeName || 'captain';
      if (tradeModal) tradeModal.hidden = false;
    };
  });

  // Boarding — fatal action, gated by a confirm.
  container.querySelectorAll<HTMLButtonElement>('[data-board-captain]').forEach(btn => {
    btn.onclick = () => {
      const token = btn.dataset.boardCaptain!;
      const name = btn.dataset.boardName || 'captain';
      const ok = window.confirm(
        `Board ${name}? Grapples will be thrown and there will be no quarter. `
        + `Your crew may die. The losing ship is lost with all hands.`,
      );
      if (ok) conn.send({ type: 'board', targetCaptainToken: token });
    };
  });

  if (tradeSend) {
    tradeSend.onclick = () => {
      if (!tradeTargetToken || !tradeItemSelect || !tradeQty || !tradePrice) return;
      const itemId = tradeItemSelect.value;
      const quantity = Math.max(1, parseInt(tradeQty.value, 10) || 1);
      const priceReales = Math.max(0, parseInt(tradePrice.value, 10) || 0);
      if (!itemId) return;
      conn.send({ type: 'propose_trade', receiverToken: tradeTargetToken, itemId, quantity, priceReales });
      closeTradeModal();
    };
  }
}

function formatStores(captain: CaptainSave): string {
  const parts: string[] = [];
  const items: Array<[string, string]> = [
    ['hardtack', 'hardtack'],
    ['dried_beans', 'beans'],
    ['salted_pork', 'pork'],
    ['fresh_water', 'water'],
    ['rum', 'rum'],
    ['fish_caught', 'fish'],
    ['whale_meat', 'whale meat'],
  ];
  for (const [id, name] of items) {
    const q = ownedOf(captain, id);
    if (q > 0) parts.push(`${q.toFixed(0)} ${name}`);
  }
  return parts.length === 0 ? '— empty' : parts.join(', ');
}

function moraleClass(morale: number): string {
  if (morale >= 75) return 'high';
  if (morale >= 50) return 'content';
  if (morale >= 25) return 'restless';
  if (morale > 0) return 'dangerous';
  return 'mutinous';
}

function renderMorale(morale: number): string {
  const label = morale >= 75 ? 'High' : morale >= 50 ? 'Content' : morale >= 25 ? 'Restless' : morale > 0 ? 'Dangerous' : 'Mutinous';
  return `${label} (${Math.round(morale)})`;
}

function formatAntiscurvy(captain: CaptainSave): string {
  const parts: string[] = [];
  const lemons = ownedOf(captain, 'lemons');
  const kraut = ownedOf(captain, 'sauerkraut');
  const lime = ownedOf(captain, 'lime_juice');
  if (lemons > 0) parts.push(`${lemons.toFixed(0)} lemons`);
  if (kraut > 0) parts.push(`${kraut.toFixed(0)} sauerkraut`);
  if (lime > 0) parts.push(`${lime.toFixed(0)} lime juice`);
  if (parts.length === 0) {
    return captain.scurvyGraceDays > 0
      ? `none — ${captain.scurvyGraceDays.toFixed(1)} days without`
      : 'none';
  }
  return parts.join(', ');
}
